import styled from "styled-components";
import React, { useCallback, useContext, useEffect, useState, useRef, useMemo } from "react";
import AppContext from "AppContext";
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import SearchIcon from '@mui/icons-material/Search';
import PageviewIcon from '@mui/icons-material/Pageview';
import _ from 'lodash';
import { useProfileFiltersUI } from 'hooks/useProfileFiltersUI';
import { provinces, indigenousNations } from 'website/siteutilities';
import { debounce } from 'lodash';
import { COMMON_EMAIL_REGEX, INVALID_ENTRIES } from 'utilities/profilefilterutils';
import BulkActions from './BulkActions';

// 1. Base styled components first
const FiltersContainers = styled.div.attrs({
  className: 'FiltersContainers'
})`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  border-radius: 4px;
  box-sizing: border-box;
  padding: 12px 12px 4px 12px;
  gap: 5px;
`;

const FilterContainer = styled.div.attrs({
  className: 'FilterContainer'
})`
  position: relative;
  display: flex;
`;

const FilterDropdown = styled.div.attrs({
  className: 'FilterDropdown'
})`
  position: absolute;
  top: 100%;
  left: 0;
  background: white;
  border-radius: 3px;
  box-shadow: 0px 1px 3px 2px rgba(0, 0, 0, 0.05);
  min-width: 200px;
  z-index: 10;
  margin-top: 5px;
  max-height: 300px;
  overflow-y: auto;
  opacity: 0;
  transform: translateY(-2px);
  visibility: hidden;
  transition: all 0.2s ease-in-out;
  padding: 8px 5px;
  display: flex;
  flex-direction: column;
  gap: 5px;
  ${props => props.$isOpen && `
    opacity: 1;
    transform: translateY(0);
    visibility: visible;
  `}

  &::-webkit-scrollbar {
    width: 5px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f5f5f5;
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: black;
    border-radius: 2px;
  }
`;

// 2. Components that extend the base components
const SearchContainer = styled(FilterContainer)`
  margin-left: auto;
  margin-right: 10px;
  display: flex;
  gap: 5px;
  flex: 1;
  max-width: 50%;
  &:hover {
    cursor: text !important;
  }
  .Filter {
    width: auto;
  }
`;

const SearchDropdown = styled(FilterDropdown)`
  padding: 0px;
  right: 0;
  transition: opacity 0.15s ease-in-out, transform 0.15s ease-in-out, visibility 0.15s ease-in-out;
`;

// Now we can define components that depend on FilterContainer
const SearchInputContainer = styled.div`
  position: relative;
  width: 100%;
  padding: 3px;
  box-sizing: border-box;
`;

const SearchInput = styled.input.attrs({
  className: 'SearchInput'
})`
  background: #FFF;
  border: 1px solid #dadada;
  border-radius: 3px;
  padding: 8px 12px 8px 32px;
  padding-right: 30px;
  font-family: "PT Sans";
  font-size: 11px;
  font-weight: 900;
  letter-spacing: 0.11px;
  color: #000;
  width: 100%;
  box-sizing: border-box;
  &::placeholder {
    color: #8C8C8C;
  }
  &:focus {
    outline: none;
    box-shadow: 0 0 0 1px #000;
  }
`;

const ClearButton = styled.button.attrs({
  className: 'ClearButton'
})`
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #e0e0e0;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  padding-bottom: 2px;
  color: white;
  font-size: 12px;
  font-weight: bold;
  line-height: 3px;

  &:hover {
    background: #c9c9c9;
  }

  &::before {
    content: '×';
  }
`;

const StyledPageviewIcon = styled(PageviewIcon)`
  position: absolute;
  left: 8px;
  top: 50%;
  transform: translateY(-50%);
  width: 16px !important;
  height: 16px !important;
  color: #8C8C8C;
`;

export const SearchBox = ({ onSearch, onReset, setOpenFilter, openFilter }) => {
  const [inputValue, setInputValue] = useState('');
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  const debouncedSearch = useCallback(
    _.debounce((value) => {
      const trimmedValue = value.trim();
      onSearch(trimmedValue || null);
    }, 300),
    [onSearch]
  );

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest('.SearchContainer')) {
        setOpenFilter(null);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [setOpenFilter]);

  const handleChange = (e) => {
    const value = e.target.value;
    setInputValue(value);
    debouncedSearch(value);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      setOpenFilter(null);
    }
  };

  const handleClear = () => {
    setInputValue('');
    debouncedSearch('');
  };

  useEffect(() => {
    if (onReset) {
      setInputValue('');
      setOpenFilter(null);
      debouncedSearch('');
    }
  }, [onReset, debouncedSearch, setOpenFilter]);

  const handleSearchClick = (e) => {
    e.stopPropagation();
    setOpenFilter('search');
  };

  // Focus input when dropdown opens
  useEffect(() => {
    if (openFilter === 'search' && inputRef.current) {
      const timeoutId = setTimeout(() => {
        inputRef.current?.focus();
      }, 50);
      return () => clearTimeout(timeoutId);
    }
  }, [openFilter]);

  const isOpen = openFilter === 'search';

  return (
    <SearchContainer className="SearchContainer" onClick={(e) => e.stopPropagation()}>
      <Filter
        className={`search ${isOpen ? 'active' : ''}`}
        onClick={handleSearchClick}
      >
        <span>{inputValue.trim() || 'Search'}</span>
        <PageviewIcon fontSize="small" />
      </Filter>
      <SearchDropdown ref={dropdownRef} $isOpen={isOpen}>
        <SearchInputContainer>
          <SearchInput
            ref={inputRef}
            placeholder="Search profiles..."
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            value={inputValue}
          />
          <StyledPageviewIcon />
          {inputValue.trim() && <ClearButton onClick={handleClear} />}
        </SearchInputContainer>
      </SearchDropdown>
    </SearchContainer>
  );
};

// Filters

const FilterOption = styled.div.attrs({
  className: 'FilterOption'
})`
  cursor: pointer;
  font-size: 12px;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 10px;
  width: fit-content;
  border-radius: 3px;
  padding: 5px 10px;
  font-weight: ${props => props.$isProfileData ? '400' : '900'};
  ${props => props.$isProfileData && `
    font-size: 11px;
    margin-left: 10px;
  `}
  ${props => props.$disabled && `
    opacity: 0.5;
    cursor: not-allowed;
    &:hover {
      background: transparent;
    }
  `}
  &:hover {
    background: #f1f1f1;
  }
  &.selected {
    background: black;
    color: white;
    font-weight: 700;
  }
`;

const LocationCount = styled.span.attrs({
  className: 'LocationCount'
})`
  background: ${props => props.selected ? 'white' : '#f1f1f1'};
  color: ${props => props.selected ? '#3C3C3C' : '#8c8c8c'};
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  font-size: 11px;
  font-weight: 900;
  flex-shrink: 0;
`;

export const Filter = styled.div.attrs({
  className: 'Filter'
})`
  background: #FFF;
  box-shadow: 0px 1px 3px 2px rgba(0, 0, 0, 0.05);
  width: fit-content;
  color: #000;
  font-family: "PT Sans";
  font-size: 11px;
  font-weight: 900;
  letter-spacing: 0.11px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-radius: 3px;
  padding: 7px 5px 7px 12px;
  cursor: pointer;
  text-transform: capitalize;
  &.sort {
    color: #8C8C8C; 
  }
  &.search {
    min-width: 80px;
    flex: 1;
    max-width: 80%;
    width: 100% !important;
    span {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }
  svg {
    justify-self: flex-end;
    margin-left: 5px;
    width: 15px;
    flex-shrink: 0;
  }
  &:hover, &.active {
    background: #3C3C3C;
    color: white;
  }
`;

const SortOption = styled(FilterOption).attrs({
  className: 'SortOption'
})`
  text-transform: capitalize;
  font-size: 11px;
  letter-spacing: 0.11px;
  
  .status-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    margin-right: 5px;
  }
`;

const ResetLink = styled.span.attrs({
  className: 'ResetLink'
})`
  text-decoration: underline;
  cursor: pointer;
  font-size: 11px;
  font-weight: 900;
  color: #8C8C8C;
  align-self: center;
  margin: 0 15px;
  &:hover {
    color: black;
  }
`;

// Add this helper function at the top level of the file
const getLocationOrder = (location) => {
  // Check Indigenous Nations first
  const indigenousNation = Object.values(indigenousNations).find(
    nation => nation.name === location ||
      nation.communities.some(c => c.name === location)
  );
  if (indigenousNation) {
    return indigenousNation.uid;
  }

  // Check Provinces and their cities
  const province = Object.values(provinces).find(
    prov => prov.name === location ||
      prov.cities.some(c => c.name === location)
  );
  if (province) {
    return province.uid;
  }

  return 999; // Default order for unknown locations
};

// Add these helper functions at the top level
const getLocationGroups = (locations, locationCounts) => {
  const groups = {
    indigenous: [],
    provinces: new Map(), // Change to Map to maintain province order
    international: []
  };

  // Helper to get province order
  const getProvinceOrder = () => {
    // Define exact order matching siteutilities UIDs
    const orderByUid = [16, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 17, 18, 19];

    return Object.entries(provinces)
      .filter(([key]) => key !== 'International')
      .map(([_, province]) => ({
        name: province.name,
        uid: province.uid,
        image: province.image
      }))
      .sort((a, b) => {
        const indexA = orderByUid.indexOf(a.uid);
        const indexB = orderByUid.indexOf(b.uid);
        return indexA - indexB;
      });
  };

  const provinceOrder = getProvinceOrder();

  // Initialize all provinces with 0 counts
  provinceOrder.forEach(province => {
    groups.provinces.set(province.name, {
      ...province,
      count: 0,
      cities: []
    });
  });

  locations.forEach(location => {
    // Check if it's an indigenous nation or community
    const indigenousNation = Object.values(indigenousNations).find(
      nation => nation.name === location || nation.communities.some(c => c.name === location)
    );
    if (indigenousNation) {
      const isParent = indigenousNation.name === location;
      groups.indigenous.push({
        name: location,
        count: locationCounts[location],
        image: isParent ? indigenousNation.image : null,
        parent: isParent ? null : indigenousNation.name,
        uid: isParent ? indigenousNation.uid : null
      });
      return;
    }

    // Check if it's international
    if (location === 'International' ||
      provinces.International.cities.some(c => c.name === location)) {
      const isParent = location === 'International';
      groups.international.push({
        name: location,
        count: locationCounts[location],
        image: isParent ? provinces.International.image : null,
        parent: isParent ? null : 'International',
        uid: isParent ? provinces.International.uid : null
      });
      return;
    }

    // Handle provinces and cities
    const province = provinceOrder.find(p =>
      p.name === location || provinces[p.name]?.cities.some(c => c.name === location)
    );

    if (province) {
      const provinceData = groups.provinces.get(province.name);
      if (location === province.name) {
        // Update province count
        provinceData.count = locationCounts[location];
      } else {
        // Add city to province
        provinceData.cities.push({
          name: location,
          count: locationCounts[location],
          parent: province.name
        });
        // Sort cities by count then alphabetically
        provinceData.cities.sort((a, b) =>
          b.count - a.count || a.name.localeCompare(b.name)
        );
      }
      groups.provinces.set(province.name, provinceData);
    }
  });

  // Sort indigenous nations
  groups.indigenous.sort((a, b) => {
    if (b.count !== a.count) return b.count - a.count;
    if ((a.parent === null) !== (b.parent === null)) return a.parent === null ? -1 : 1;
    if (a.parent === null && b.parent === null) return a.uid - b.uid;
    return a.name.localeCompare(b.name);
  });

  // Sort international
  groups.international.sort((a, b) => {
    if (b.count !== a.count) return b.count - a.count;
    if ((a.parent === null) !== (b.parent === null)) return a.parent === null ? -1 : 1;
    return a.name.localeCompare(b.name);
  });

  // Add helper to calculate total province count
  const getProvinceTotal = (provinceData) => {
    const directCount = provinceData.count || 0;
    const citiesCount = provinceData.cities.reduce((sum, city) => sum + city.count, 0);
    return directCount + citiesCount;
  };

  // Update the sorting in the return statement
  const provincesWithTotals = Array.from(groups.provinces.values())
    .map(province => ({
      ...province,
      totalCount: getProvinceTotal(province)
    }))
    .filter(province => province.totalCount > 0)
    .sort((a, b) => {
      // Sort by the original order from siteutilities
      const orderByUid = [16, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 17, 18, 19];
      const indexA = orderByUid.indexOf(a.uid);
      const indexB = orderByUid.indexOf(b.uid);
      return indexA - indexB;
    });

  return {
    indigenous: groups.indigenous,
    provinces: provincesWithTotals,
    international: groups.international
  };
};

// Add this with the other styled components at the top of the file
const LocationSectionHeader = styled.div`
  padding: 8px 10px 5px;
  font-size: 11px;
  font-weight: 700;
  color: #8c8c8c;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-top: 1px solid #eee;
  margin-top: 5px;
  background: #f9f9f9;
`;

// Add these styled components after existing styled components
const InfoIcon = styled.span.attrs({
  className: 'InfoIcon'
})`
  font-size: 11px;
  color: #8c8c8c;
  margin-left: 5px;
  cursor: help;
  &:hover {
    color: #000;
  }
`;

const FiltersWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const StyledBulkActions = styled.div`
  display: flex;
  align-items: center;
  margin-left: auto;
`;

export const ProfileFilters = ({
  profiles,
  filteredProfiles,
  onFilterChange,
  adminOrgs,
  onSearch,
  filterState,
  onFilterStateChange,
  counts,
  setProfiles
}) => {
  const [openFilter, setOpenFilter] = useState(null);
  const [resetTrigger, setResetTrigger] = useState(false);

  const getStatusLabel = (status) => {
    if (status === 'Status') return status;
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  const getStatusDotColor = (status) => {
    switch (status) {
      case 'active':
        return '#59DE51';
      case 'accepted':
        return '#4CAF50';
      case 'waitlist':
        return '#8C8C8C';
      case 'submitted':
        return '#FFC107';
      case 'rejected':
        return '#F44336';
      case 'banned':
        return '#000000';
      default:
        return '#f1f1f1';
    }
  };

  // Memoize domain counting calculation
  const domainCounts = useMemo(() => {
    if (!profiles || !counts) return {};

    const profilesForCounting = filteredProfiles || profiles;
    const countsByDomain = {
      'Common Email Providers': 0,
      'Custom': 0
    };

    profilesForCounting.forEach(profile => {
      const email = profile.email;
      if (email && !INVALID_ENTRIES.includes(email.toLowerCase().trim())) {
        if (COMMON_EMAIL_REGEX.test(email)) {
          countsByDomain['Common Email Providers']++;
        } else {
          countsByDomain['Custom']++;
        }
      }
    });

    return countsByDomain;
  }, [profiles, filteredProfiles]);

  // Update counts with debouncing
  useEffect(() => {
    if (!counts) return;
    
    const updateCounts = debounce(() => {
      counts.domainCounts = domainCounts;
    }, 300);

    updateCounts();
    return () => updateCounts.cancel();
  }, [domainCounts, counts]);

  // Debounced click outside handler
  useEffect(() => {
    const handleClickOutside = debounce((e) => {
      if (!e.target.closest('.FilterContainer')) {
        setOpenFilter(null);
      }
    }, 150);

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
      handleClickOutside.cancel();
    };
  }, []);

  // Memoize filter label calculation
  const getFilterLabel = useCallback((type) => {
    const filters = filterState[type];
    if (filters.length === 0) {
      if (type === 'status') return 'Status';
      return type.charAt(0).toUpperCase() + type.slice(1) + 's';
    }
    if (filters.length === 1) {
      return type === 'org' ? filters[0].name : filters[0];
    }
    const typeName = type === 'org' ? 'Organizations' :
      type === 'status' ? 'Status' :
      type.charAt(0).toUpperCase() + type.slice(1) + 's';
    return `${filters.length} ${typeName}`;
  }, [filterState]);

  const locationGroups = getLocationGroups(
    profiles ? _.uniq(profiles.map(profile => profile.profiledata.userLocation))
      .filter(location => counts.locationCounts[location] > 0) : [],
    counts.locationCounts
  );

  const sortOptions = [
    { value: null, label: 'Sort' },
    { value: 'newest', label: 'Newest First' },
    { value: 'oldest', label: 'Oldest First' },
    { value: 'status', label: 'By Status' },
    { value: 'active', label: 'Active First' },
    { value: 'accepted', label: 'Accepted First' },
    { value: 'waitlist', label: 'Waitlist First' },
    { value: 'submitted', label: 'Submitted First' },
    { value: 'rejected', label: 'Rejected First' },
    { value: 'banned', label: 'Banned First' },
  ];

  const getSortLabel = () => {
    const option = sortOptions.find(opt => opt.value === filterState.sort);
    return option ? option.label : 'Sort By';
  };

  const roles = profiles ?
    _.uniq(profiles.map(profile => profile.profiledata.userRoles))
      .filter(Boolean)
      .filter(role => counts.roleCounts[role] > 0)
      .sort() : [];
  if (profiles?.some(p => p.profiledata.userRoles === 'Founder' && p.profiledata.gender === 'Female') && counts.roleCounts['Women Founder'] > 0) {
    roles.push('Women Founder');
  }

  const filteredAdminOrgs = adminOrgs.filter(org => counts.orgCounts[org.id] > 0);

  const domains = ['Common Email Providers', 'Custom'].filter(domain => counts.domainCounts[domain] > 0);

  const labelMap = {
    hasWebsite: 'Has Website',
    isLinkedIn: 'LinkedIn Profile',
    notLinkedIn: 'Not LinkedIn',
    noWebsite: 'No Website',
    hasCompany: 'Has Company Name',
    hasDescription: 'Has Description',
    noCompany: 'No Company Name',
    noDescription: 'No Description',
    customEmail: 'Custom Email Domains',
    commonEmail: 'Common Email Providers'
  };

  // Memoize profile data filters
  const profileDataFilters = useMemo(() => [
    {
      section: 'Status',
      filters: ['active', 'accepted', 'waitlist', 'submitted', 'rejected', 'banned']
        .filter(status => counts.statusCounts[status] > 0)
        .map(status => ({
          id: status,
          label: getStatusLabel(status),
          count: counts.statusCounts[status] || 0,
          dotColor: getStatusDotColor(status)
        }))
    },
    {
      section: 'Website',
      filters: [
        {
          id: 'hasWebsite',
          label: 'Has Website',
          count: (filteredProfiles || profiles)?.filter(p =>
            p.profiledata.profileUrl &&
            !INVALID_ENTRIES.includes(p.profiledata.profileUrl.toLowerCase().trim())
          ).length || 0
        },
        {
          id: 'isLinkedIn',
          label: 'LinkedIn Profile',
          count: (filteredProfiles || profiles)?.filter(p =>
            p.profiledata.profileUrl &&
            p.profiledata.profileUrl.toLowerCase().includes('linkedin.com')
          ).length || 0
        },
        {
          id: 'notLinkedIn',
          label: 'Not LinkedIn',
          count: (filteredProfiles || profiles)?.filter(p =>
            p.profiledata.profileUrl &&
            !p.profiledata.profileUrl.toLowerCase().includes('linkedin.com')
          ).length || 0
        },
        {
          id: 'noWebsite',
          label: 'No Website',
          count: (filteredProfiles || profiles)?.filter(p =>
            !p.profiledata.profileUrl ||
            INVALID_ENTRIES.includes(p.profiledata.profileUrl.toLowerCase().trim())
          ).length || 0
        }
      ]
    },
    {
      section: 'Email',
      filters: [
        {
          id: 'customEmail',
          label: 'Custom Email Domains',
          count: domainCounts['Custom'] || 0
        },
        {
          id: 'commonEmail',
          label: 'Common Email Providers',
          count: domainCounts['Common Email Providers'] || 0
        }
      ]
    }
  ], [profiles, filteredProfiles, domainCounts]);

  // Memoize filter selection handler
  const handleFilterSelect = useCallback((type, value) => {
    if (type === 'sort') {
      const newSortState = {
        ...filterState,
        sort: value
      };
      onFilterStateChange(newSortState);
      onFilterChange({
        type: 'sort',
        value
      });
      return;
    }

    let newValue;
    if (value === 'All Organizations' || value === 'All Roles' ||
      value === 'All Locations' || value === 'All Status' ||
      value === 'All') {
      newValue = [];
    } else {
      const currentFilters = filterState[type];
      if (type === 'org') {
        if (currentFilters.some(f => f.id === value.id)) {
          newValue = currentFilters.filter(f => f.id !== value.id);
        } else {
          newValue = [...currentFilters, value];
        }
      } else if (type === 'profileData') {
        if (currentFilters.includes(value)) {
          newValue = currentFilters.filter(f => f !== value);
        } else {
          // Handle email domain filters
          if (value === 'commonEmail' || value === 'customEmail') {
            // Remove the opposite email filter if it exists
            const oppositeFilter = value === 'commonEmail' ? 'customEmail' : 'commonEmail';
            newValue = [...currentFilters.filter(f => f !== oppositeFilter), value];
          } else {
            // Check if the value is a status
            const isStatus = ['active', 'accepted', 'waitlist', 'submitted', 'rejected', 'banned'].includes(value);
            if (isStatus) {
              // Allow multiple status selections
              newValue = [...currentFilters, value];
            } else {
              newValue = [...currentFilters, value];
            }
          }
        }
      } else {
        if (currentFilters.includes(value)) {
          newValue = currentFilters.filter(f => f !== value);
        } else {
          newValue = [...currentFilters, value];
        }
      }
    }

    const newFilterState = {
      ...filterState,
      [type]: newValue
    };
    
    onFilterStateChange(newFilterState);

    // Debounce the filter change notification
    const debouncedFilterChange = debounce(() => {
      if (type === 'org') {
        const orgIds = newValue.length > 0 ? newValue.map(org => org.id) : null;
        onFilterChange({
          type: 'organization',
          value: orgIds
        });
      } else if (type === 'profileData') {
        // Handle domain filters separately from other profile data filters
        const domainFilters = [];
        const profileDataFilters = [];
        const statusFilters = [];

        newValue.forEach(filter => {
          if (filter === 'commonEmail') {
            domainFilters.push('Common Email Providers');
          } else if (filter === 'customEmail') {
            domainFilters.push('Custom');
          } else if (['active', 'accepted', 'waitlist', 'submitted', 'rejected', 'banned'].includes(filter)) {
            statusFilters.push(filter);
          } else {
            profileDataFilters.push(filter);
          }
        });

        // Update profile data filters
        onFilterChange({
          type: 'profileData',
          value: profileDataFilters.length > 0 ? profileDataFilters : null
        });

        // Update domain filters
        onFilterChange({
          type: 'domain',
          value: domainFilters.length > 0 ? domainFilters : null
        });

        // Update status filters
        onFilterChange({
          type: 'status',
          value: statusFilters.length > 0 ? statusFilters : null
        });
      } else {
        onFilterChange({
          type,
          value: newValue.length > 0 ? newValue : null
        });
      }
    }, 150);

    debouncedFilterChange();
  }, [filterState, onFilterChange, onFilterStateChange]);

  const handleReset = () => {
    const resetState = {
      org: [],
      role: [],
      location: [],
      status: [],
      sort: null,
      profileData: []
    };
    onFilterStateChange(resetState);
    ['organization', 'role', 'location', 'status', 'sort', 'domain', 'profileData'].forEach(type =>
      onFilterChange({ type, value: null })
    );
    setResetTrigger(prev => !prev);
    onSearch('');
  };

  return (
    <FiltersWrapper>
      <FiltersContainers>
        <FilterContainer onClick={(e) => e.stopPropagation()}>
          <Filter
            className={openFilter === 'org' ? 'active' : ''}
            onClick={() => setOpenFilter(openFilter === 'org' ? null : 'org')}
          >
            <span>{getFilterLabel('org')}</span>
            <KeyboardArrowDownIcon fontSize="small" />
          </Filter>
          <FilterDropdown $isOpen={openFilter === 'org'}>
            <FilterOption
              className={filterState.org.length === 0 ? 'selected' : ''}
              onClick={() => handleFilterSelect('org', 'All Organizations')}
            >
              <span>All Organizations</span>
              <LocationCount selected={filterState.org.length === 0}>
                {counts.orgCounts['All Organizations'] || 0}
              </LocationCount>
            </FilterOption>
            {filteredAdminOrgs.map((org) => (
              <FilterOption
                key={org.id}
                className={filterState.org.some(selected => selected.id === org.id) ? 'selected' : ''}
                onClick={() => handleFilterSelect('org', org)}
              >
                <span>{org.name}</span>
                <LocationCount selected={filterState.org.some(selected => selected.id === org.id)}>
                  {counts.orgCounts[org.id] || 0}
                </LocationCount>
              </FilterOption>
            ))}
          </FilterDropdown>
        </FilterContainer>
        <FilterContainer onClick={(e) => e.stopPropagation()}>
          <Filter
            className={openFilter === 'role' ? 'active' : ''}
            onClick={() => setOpenFilter(openFilter === 'role' ? null : 'role')}
          >
            <span>{getFilterLabel('role')}</span>
            <KeyboardArrowDownIcon fontSize="small" />
          </Filter>
          <FilterDropdown $isOpen={openFilter === 'role'}>
            <FilterOption
              className={filterState.role.length === 0 ? 'selected' : ''}
              onClick={() => handleFilterSelect('role', 'All Roles')}
            >
              <span>All Roles</span>
              <LocationCount selected={filterState.role.length === 0}>
                {counts.roleCounts['All Roles'] || 0}
              </LocationCount>
            </FilterOption>
            {roles.filter(role => role !== 'All Roles').map((role) => (
              <FilterOption
                key={role}
                className={filterState.role.includes(role) ? 'selected' : ''}
                onClick={() => handleFilterSelect('role', role)}
              >
                <span>{role}</span>
                <LocationCount selected={filterState.role.includes(role)}>
                  {counts.roleCounts[role] || 0}
                </LocationCount>
              </FilterOption>
            ))}
          </FilterDropdown>
        </FilterContainer>
        <FilterContainer onClick={(e) => e.stopPropagation()}>
          <Filter
            className={openFilter === 'location' ? 'active' : ''}
            onClick={() => setOpenFilter(openFilter === 'location' ? null : 'location')}
          >
            <span>{getFilterLabel('location')}</span>
            <KeyboardArrowDownIcon fontSize="small" />
          </Filter>
          <FilterDropdown $isOpen={openFilter === 'location'}>
            <FilterOption
              className={filterState.location.length === 0 ? 'selected' : ''}
              onClick={() => handleFilterSelect('location', 'All Locations')}
            >
              <span>All Locations</span>
              <LocationCount selected={filterState.location.length === 0}>
                {counts.locationCounts['All Locations'] || 0}
              </LocationCount>
            </FilterOption>

            {/* Indigenous Nations */}
            {locationGroups.indigenous.length > 0 && (
              <LocationSectionHeader>Indigenous Nations</LocationSectionHeader>
            )}
            {locationGroups.indigenous.map((location) => (
              <FilterOption
                key={location.name}
                className={filterState.location.includes(location.name) ? 'selected' : ''}
                onClick={() => handleFilterSelect('location', location.name)}
                style={{
                  paddingLeft: location.parent ? '25px' : '10px',
                  fontSize: location.parent ? '11px' : '12px'
                }}
              >
                {location.image && <span style={{ marginRight: '5px' }}>{location.image}</span>}
                <span>{location.name}</span>
                <LocationCount selected={filterState.location.includes(location.name)}>
                  {location.count}
                </LocationCount>
              </FilterOption>
            ))}

            {/* Provinces */}
            {locationGroups.provinces.length > 0 && (
              <LocationSectionHeader>Provinces</LocationSectionHeader>
            )}
            {locationGroups.provinces.map((province) => (
              <React.Fragment key={province.name}>
                <FilterOption
                  className={filterState.location.includes(province.name) ? 'selected' : ''}
                  onClick={() => handleFilterSelect('location', province.name)}
                  style={{
                    paddingLeft: '10px',
                    fontSize: '12px'
                  }}
                >
                  {province.image && <span style={{ marginRight: '5px' }}>{province.image}</span>}
                  <span>{province.name}</span>
                  <LocationCount selected={filterState.location.includes(province.name)}>
                    {province.totalCount}
                  </LocationCount>
                </FilterOption>
                {province.cities.map((city) => (
                  <FilterOption
                    key={city.name}
                    className={filterState.location.includes(city.name) ? 'selected' : ''}
                    onClick={() => handleFilterSelect('location', city.name)}
                    style={{
                      paddingLeft: '42px',
                      fontSize: '11px',
                      fontWeight: '400'
                    }}
                  >
                    <span>{city.name}</span>
                    <LocationCount selected={filterState.location.includes(city.name)}>
                      {city.count}
                    </LocationCount>
                  </FilterOption>
                ))}
              </React.Fragment>
            ))}

            {/* International */}
            {locationGroups.international.length > 0 && (
              <LocationSectionHeader>International</LocationSectionHeader>
            )}
            {locationGroups.international.map((location) => (
              <FilterOption
                key={location.name}
                className={filterState.location.includes(location.name) ? 'selected' : ''}
                onClick={() => handleFilterSelect('location', location.name)}
                style={{
                  paddingLeft: location.parent ? '25px' : '10px',
                  fontSize: location.parent ? '11px' : '12px'
                }}
              >
                {location.image && <span style={{ marginRight: '5px' }}>{location.image}</span>}
                <span>{location.name}</span>
                <LocationCount selected={filterState.location.includes(location.name)}>
                  {location.count}
                </LocationCount>
              </FilterOption>
            ))}
          </FilterDropdown>
        </FilterContainer>
        <FilterContainer onClick={(e) => e.stopPropagation()}>
          <Filter
            className={openFilter === 'profileData' ? 'active' : ''}
            onClick={() => setOpenFilter(openFilter === 'profileData' ? null : 'profileData')}
          >
            <span>{filterState.profileData.length === 0 ? 'Profiles' :
              filterState.profileData.length === 1 ? labelMap[filterState.profileData[0]] || getStatusLabel(filterState.profileData[0]) :
                `${filterState.profileData.length} Filters`}</span>
            <KeyboardArrowDownIcon fontSize="small" />
          </Filter>
          <FilterDropdown $isOpen={openFilter === 'profileData'}>
            <FilterOption
              $isProfileData
              className={filterState.profileData.length === 0 ? 'selected' : ''}
              onClick={() => handleFilterSelect('profileData', 'All')}
            >
              <span>All Profiles</span>
              <LocationCount selected={filterState.profileData.length === 0}>
                {profiles?.length || 0}
              </LocationCount>
            </FilterOption>
            {profileDataFilters.map((section) => (
              <React.Fragment key={section.section}>
                <LocationSectionHeader>{section.section}</LocationSectionHeader>
                {section.filters.map((filter) => (
                  <FilterOption
                    $isProfileData
                    $disabled={filter.count === 0}
                    key={filter.id}
                    className={filterState.profileData.includes(filter.id) ? 'selected' : ''}
                    onClick={() => filter.count > 0 && handleFilterSelect('profileData', filter.id)}
                  >
                    {section.section === 'Status' && (
                      <div style={{
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        backgroundColor: filter.dotColor,
                        marginRight: '5px'
                      }} />
                    )}
                    <span>{filter.label}</span>
                    <LocationCount selected={filterState.profileData.includes(filter.id)}>
                      {filter.count}
                    </LocationCount>
                  </FilterOption>
                ))}
              </React.Fragment>
            ))}
          </FilterDropdown>
        </FilterContainer>
        <ResetLink onClick={handleReset}>
          Reset
        </ResetLink>
        <SearchContainer onClick={(e) => e.stopPropagation()}>
          <SearchBox
            onSearch={onSearch}
            onReset={resetTrigger}
            setOpenFilter={setOpenFilter}
            openFilter={openFilter}
          />
          <FilterContainer>
            <Filter
              className={`sort ${openFilter === 'sort' ? 'active' : ''}`}
              onClick={() => setOpenFilter(openFilter === 'sort' ? null : 'sort')}
            >
              <span>{getSortLabel()}</span>
              <KeyboardArrowDownIcon fontSize="small" />
            </Filter>
            <FilterDropdown $isOpen={openFilter === 'sort'}>
              {sortOptions.map((option) => (
                <SortOption
                  key={option.value}
                  className={filterState.sort === option.value ? 'selected' : ''}
                  onClick={() => handleFilterSelect('sort', option.value)}
                >
                  <span>{option.label}</span>
                </SortOption>
              ))}
            </FilterDropdown>
          </FilterContainer>
        </SearchContainer>
        <StyledBulkActions>
          <BulkActions 
            filteredProfiles={filteredProfiles} 
            adminOrgs={adminOrgs} 
            onSuccess={(result) => {
              if (result && result.data) {
                const updatedProfilesMap = result.data.reduce((acc, profile) => {
                  acc[profile.profile_id] = profile;
                  return acc;
                }, {});
                
                setProfiles(prevProfiles => 
                  prevProfiles.map(profile => {
                    if (updatedProfilesMap[profile.id]) {
                      return {
                        ...profile,
                        status: updatedProfilesMap[profile.id].status
                      };
                    }
                    return profile;
                  })
                );
              }
            }}
          />
        </StyledBulkActions>
      </FiltersContainers>
    </FiltersWrapper>
  );
};

// Metrics
const MetricsContainers = styled.div.attrs({
  className: 'MetricsContainers'
})`
  display: flex;
  flex-direction: row;
  border-radius: 4px;
  box-sizing: border-box;
  padding: 6px 0px 0px 10px;
  gap: 5px;
  margin-bottom: 5px;
  padding-bottom: 10px;
`;

const MetricLabel = styled.span.attrs({
  className: 'MetricLabel'
})`
  color: #000;
  font-family: "PT Sans";
  font-size: 12px;
  font-style: normal;
  font-weight: 700;
  line-height: normal;
  letter-spacing: 0.12px;
  text-transform: uppercase;
`;

const MetricStat = styled.span.attrs({
  className: 'MetricStat'
})`
  color: #000;
  font-family: "PT Sans";
  font-size: 24px;
  font-style: normal;
  font-weight: 700;
  line-height: normal;
  letter-spacing: 0.24px;
  margin-left: -2px;
`;

const Metric = styled.div.attrs({
  className: 'Metric'
})`
  border-radius: 4px;
  background: #F3F3F3;
  padding: ${props => props.$compact ? '8px 15px 6px' : '10px 20px 8px'};
  gap: 3px;
  display: flex;
  flex-direction: column;
  width: 100%;
  text-align: right;
  cursor: pointer;
  transition: all 0.2s ease-in-out;

  &:hover {
    background: #E0E0E0;
  }

  &.active {
    background: #000;
    .MetricLabel, .MetricStat {
      color: white;
    }
  }

  ${props => props.$compact && `
    .MetricStat {
      display: none;
    }
  `}
`;

export const ProfileMetrics = ({ onFilterChange, activeFilters, counts, compact }) => {
  const { activeMetric, handleMetricClick } = useProfileFiltersUI();
  const [animatedCounts, setAnimatedCounts] = useState({
    newThisWeek: 0,
    submitted: 0,
    accepted: 0,
    active: 0,
    waitlist: 0,
    total: 0
  });

  // Debounced counts update
  const debouncedCounts = useMemo(() => 
    _.debounce((newCounts) => {
      if (!newCounts) return;

      const duration = 500;
      const steps = 20;
      const interval = duration / steps;
      let currentStep = 0;

      const timer = setInterval(() => {
        currentStep++;
        const progress = currentStep / steps;

        setAnimatedCounts(prev => ({
          newThisWeek: Math.round(newCounts.newThisWeek * progress),
          submitted: Math.round(newCounts.submitted * progress),
          accepted: Math.round(newCounts.accepted * progress),
          active: Math.round(newCounts.active * progress),
          waitlist: Math.round(newCounts.waitlist * progress),
          total: Math.round(newCounts.total * progress)
        }));

        if (currentStep === steps) {
          clearInterval(timer);
          setAnimatedCounts(newCounts);
        }
      }, interval);
    }, 300),
    []
  );

  // Update counts with debouncing
  useEffect(() => {
    debouncedCounts(counts);
    return () => debouncedCounts.cancel();
  }, [counts, debouncedCounts]);

  return (
    <MetricsContainers>
      <Metric
        onClick={() => handleMetricClick('newThisWeek', onFilterChange)}
        className={activeMetric === 'newThisWeek' ? 'active' : ''}
        $compact={compact}
      >
        <MetricLabel>
          {compact ? `This Week: ${animatedCounts.newThisWeek}` : 'This Week'}
        </MetricLabel>
        <MetricStat>{animatedCounts.newThisWeek ? "+" : ""} {animatedCounts.newThisWeek}</MetricStat>
      </Metric>
      <Metric
        onClick={() => handleMetricClick('submitted', onFilterChange)}
        className={activeMetric === 'submitted' ? 'active' : ''}
        $compact={compact}
      >
        <MetricLabel>
          {compact ? `Submitted: ${animatedCounts.submitted}` : 'Submitted'}
        </MetricLabel>
        <MetricStat>{animatedCounts.submitted}</MetricStat>
      </Metric>
      <Metric
        onClick={() => handleMetricClick('accepted', onFilterChange)}
        className={activeMetric === 'accepted' ? 'active' : ''}
        $compact={compact}
      >
        <MetricLabel>
          {compact ? `Accepted: ${animatedCounts.accepted}` : 'Accepted'}
        </MetricLabel>
        <MetricStat>{animatedCounts.accepted}</MetricStat>
      </Metric>
      <Metric
        onClick={() => handleMetricClick('active', onFilterChange)}
        className={activeMetric === 'active' ? 'active' : ''}
        $compact={compact}
      >
        <MetricLabel>
          {compact ? `Active: ${animatedCounts.active}` : 'Active'}
        </MetricLabel>
        <MetricStat>{animatedCounts.active}</MetricStat>
      </Metric>
      <Metric
        onClick={() => handleMetricClick('waitlist', onFilterChange)}
        className={activeMetric === 'waitlist' ? 'active' : ''}
        $compact={compact}
      >
        <MetricLabel>
          {compact ? `Waitlist: ${animatedCounts.waitlist}` : 'Waitlist'}
        </MetricLabel>
        <MetricStat>{animatedCounts.waitlist}</MetricStat>
      </Metric>
      <Metric
        onClick={() => handleMetricClick('total', onFilterChange)}
        className={activeMetric === 'total' ? 'active' : ''}
        $compact={compact}
      >
        <MetricLabel>
          {compact ? `Total: ${animatedCounts.total}` : 'Total'}
        </MetricLabel>
        <MetricStat>{animatedCounts.total}</MetricStat>
      </Metric>
    </MetricsContainers>
  );
};